/* eslint-disable camelcase */
/**
 * @author Daniel Aguirre
 * @file index.jsx
 * @description Appointment Notification View Page.
 */

// Dependencies
import PropTypes from 'prop-types';
import { BellOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { ReactComponent as BellOn } from '@nephro/assets/bell-on.svg';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import moment from 'moment';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from 'antd';
import { setStorageItem } from 'commons/helpers/storage-helper';
import { hasActiveVideoCall } from 'commons/helpers/utils';

/**
 * Appointment Notification View
 * @param { Object } AppointmentActions - Appointment Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const AppointmentNotificationView = ({
  calendarActions,
  appointmentsZoomActions
}) => {
  const { getAgendaMatePatient, getPatientAppointmentsArray } = calendarActions;
  const { getMultipleZoomVideoCallById } = appointmentsZoomActions;
  const [flag, setFlag] = useState(true);
  const [hasAppointments, setHasAppointments] = useState(false);
  const location = useLocation();
  let activeVideocall = false;

  const addZoomMeetingId = (appointments) => {
    getMultipleZoomVideoCallById({ appointments }, (_, zoomResp) => {
      if (zoomResp) {
        zoomResp.forEach((item, index) => {
          const zoomMeetingId = item?.data?.id_zoom_call;
          const itemAppointment = appointments[index];
          const current = moment.utc(itemAppointment?.cap_horainicio);
          const time = {
            cap_fecha: current.format('YYYY-MM-DD'),
            cap_hora: current.format('h:mm a')
          };
          activeVideocall = hasActiveVideoCall(time) && zoomMeetingId;
          if (activeVideocall) {
            setStorageItem('activeVideocall', itemAppointment.id);
          }

          const currentTime = moment().utc(true);
          const hours = current.diff(currentTime, 'hours');
          const diffValidation = hours <= 48 && hours >= 0;
          if (diffValidation && zoomMeetingId) {
            setHasAppointments(diffValidation);
          }
        });
      }
    });
  };

  const onGetPatientAppointmentsList = (_, response) => {
    setStorageItem('activeVideocall', '');
    const quotes = response?.data?.data?.quotes;
    if (quotes) {
      addZoomMeetingId(quotes);
    }
  };

  const onLoadAppointmentList = (_, response) => {
    if (response?.data?.success) {
      const { paciente } = response?.data?.data;
      const data = { patientId: paciente.id };
      getPatientAppointmentsArray(data, onGetPatientAppointmentsList);
    }
  };

  const onGetAgendaMatePatientInfo = ({
    id_document,
    identification_type: { id_agenda_mate }
  }) => {
    const data = {
      documentNumber: id_document,
      documentTypeId: id_agenda_mate
    };
    getAgendaMatePatient(data, onLoadAppointmentList);
  };

  useEffect(async () => {
    if (!flag) {
      const patientInfo = await getStorageItem('patientInfo');
      onGetAgendaMatePatientInfo(patientInfo);
    }
  }, [location]);

  useEffect(async () => {
    const patientInfo = await getStorageItem('patientInfo');
    const adminInfo = await getStorageItem('adminInfo');
    if (patientInfo && !adminInfo && flag) {
      setFlag(false);
      onGetAgendaMatePatientInfo(patientInfo);
    }
  });

  return (
    <span className="mr-3 sm:mr-8">
      {hasAppointments ? (
        <Link to="/main/appointments">
          <Tooltip title="Tienes citas programadas dentro de las próximas 48 horas">
            <BellOn />
          </Tooltip>
        </Link>
      ) : (
        <BellOutlined className="text-2xl" style={{ color: 'white' }} />
      )}
    </span>
  );
};

/**
 * Component definition of proptypes.
 */
AppointmentNotificationView.propTypes = {
  calendarActions: PropTypes.shape({
    getPatientAppointmentsArray: PropTypes.func.isRequired,
    getAgendaMatePatient: PropTypes.func.isRequired
  }).isRequired,
  appointmentsZoomActions: PropTypes.shape({
    getMultipleZoomVideoCallById: PropTypes.func.isRequired
  }).isRequired
};
export default AppointmentNotificationView;
